import React, { useState } from "react";
import {
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  Person,
  Logout,
  Settings,
  KeyboardArrowDown,
} from "@mui/icons-material";
import { Link, useNavigate } from "react-router-dom";
import * as myApi from "../../Api";
import ConfirmationDialog from "../../UI/ConfirmationDialog";

/**
 * Modern MUI User Profile Dropdown
 * Features:
 * - Avatar with user initial
 * - Dropdown menu with user info
 * - Profile link
 * - Settings link
 * - Logout with confirmation
 */
export default function Profile({ user }) {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = () => {
    handleClose();
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    myApi.removeToken();
    myApi.removeUser();
    navigate("/auth/sign-in/");
  };

  const userInitial = user?.name?.charAt(0) || "U";
  const userName = user?.name || "User";
  const userEmail = user?.email || "";
  const userRole = user?.role === "admin" ? "Administrator" : "Participant";

  return (
    <>
      <Tooltip title="Account">
        <IconButton
          onClick={handleClick}
          size="small"
          aria-controls={open ? "account-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          sx={{ ml: 1 }}
        >
          <Avatar
            src={user?.profile_picture || undefined}
            sx={{
              width: 36,
              height: 36,
              bgcolor: "grey.400",
              fontSize: "0.875rem",
            }}
          >
            {userInitial}
          </Avatar>
          <KeyboardArrowDown fontSize="small" sx={{ ml: 0.5 }} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        disableScrollLock
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 240,
            borderRadius: 2,
            mt: 1.5,
            right: 8,
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {/* User Info Header */}
        <Box sx={{ px: 2, py: 1.5, bgcolor: "grey.50" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              src={user?.profile_picture || undefined}
              sx={{
                width: 48,
                height: 48,
                bgcolor: "grey.400",
                fontSize: "1.25rem",
              }}
            >
              {userInitial}
            </Avatar>
            <Box sx={{ overflow: "hidden" }}>
              <Typography variant="subtitle2" fontWeight={600} noWrap>
                {userName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {userRole}
              </Typography>
            </Box>
          </Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
            noWrap
          >
            {userEmail}
          </Typography>
        </Box>

        <Divider />

        {/* Menu Items */}
        <MenuItem component={Link} to="/profile/">
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="My Profile" />
        </MenuItem>

        <MenuItem component={Link} to="/settings/">
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Settings" />
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleLogoutClick} sx={{ color: "error.main" }}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </MenuItem>
      </Menu>

      <ConfirmationDialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        onConfirm={handleLogoutConfirm}
        title="Logout Confirmation"
        message="Are you sure you want to logout? You will need to sign in again to access your account."
        confirmText="Logout"
        cancelText="Cancel"
        variant="warning"
      />
    </>
  );
}
